import type { NextPage } from "next";
import Head from "next/head";
import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { toast } from "react-toastify";
import { getAllValidListings } from "thirdweb/extensions/marketplace";
import { MarketplaceContract } from "../../utils/constants";

/* ============================================================
   MARKETPLACE — "Real Cosmos" registry browser.
   Ported from the Claude Design (updated) prototype and wired to
   live on-chain listings (getAllValidListings). Facets are derived
   from the actual listing data, so only populated filters render.
   ============================================================ */

type TKind = "MARS" | "MOON" | "STAR" | "AST" | "GAL" | "CON" | "EXO" | "OTHER";
interface Attr { trait_type: string; value: unknown }
interface Asset {
  id: string;
  name: string;
  image: string;
  price: number;
  priceDisplay: string;
  symbol: string;
  type: TKind;
  category?: string;
  feature?: string;
  coord?: string;
  attributes: Attr[];
}

const TYPE_LABEL: Record<TKind, string> = { MARS: "MARS", MOON: "MOON", STAR: "STAR", AST: "ASTEROID", GAL: "GALAXY", CON: "CONSTEL.", EXO: "EXOPLANET", OTHER: "ASSET" };
const TYPE_HUE: Record<TKind, string> = { MARS: "var(--mars)", MOON: "var(--moon)", STAR: "var(--sp-blue)", AST: "var(--sp-amber)", GAL: "var(--sp-cyan)", CON: "var(--sp-green)", EXO: "var(--accent)", OTHER: "var(--accent)" };
const REG_LABEL: Record<TKind, string> = { MARS: "Mars Land", MOON: "Moon Land", STAR: "Stars", AST: "Asteroids", GAL: "Galaxies", CON: "Constellations", EXO: "Exoplanets", OTHER: "Other Assets" };
const REG_ORDER: TKind[] = ["MARS", "MOON", "STAR", "AST", "GAL", "CON", "EXO", "OTHER"];
const SORTS = [
  { k: "recent", label: "Recent" },
  { k: "price-asc", label: "Price ↑" },
  { k: "price-desc", label: "Price ↓" },
  { k: "az", label: "A–Z" },
];

function ipfs(url?: string): string {
  if (!url || typeof url !== "string") return "";
  let u = url.startsWith("@") ? url.slice(1) : url;
  if (u.startsWith("ipfs://")) u = u.replace("ipfs://", "https://gateway.pinata.cloud/ipfs/");
  return u;
}
function deriveType(name: string, category?: string): TKind {
  const s = `${name} ${category || ""}`.toLowerCase();
  if (s.includes("mars")) return "MARS";
  if (/moon|lunar|earthsmoon/.test(s)) return "MOON";
  if (/asteroid|\bneo\b|eros|vesta|ceres|bennu|pallas/.test(s)) return "AST";
  if (/galax|nebula|\bngc\b|messier/.test(s)) return "GAL";
  if (/constellation/.test(s)) return "CON";
  if (/exoplanet/.test(s)) return "EXO";
  if (/\bstar\b|\bhip\b/.test(s)) return "STAR";
  return "OTHER";
}
function findAttr(attrs: Attr[], re: RegExp): string | undefined {
  const a = attrs.find((x) => x && typeof x.trait_type === "string" && re.test(x.trait_type));
  return a && a.value != null ? String(a.value) : undefined;
}
const vars = (o: Record<string, string | number>) => o as React.CSSProperties;
const hideOnError = (e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = "none"; };

/* ---------- visual (real image with tinted-orb fallback) ---------- */
function AssetVisual({ a, badge }: { a: Asset; badge?: React.ReactNode }) {
  return (
    <>
      <div className="asset-fallback"><div className="asset-orb" /></div>
      {a.image && <img className="asset-img" src={a.image} alt={a.name} loading="lazy" onError={hideOnError} />}
      {badge}
    </>
  );
}

/* ---------- asset card ---------- */
function AssetCard({ a, onOpen }: { a: Asset; onOpen: (a: Asset) => void }) {
  const hue = TYPE_HUE[a.type];
  return (
    <button className="asset-card panel--hover" onClick={() => onOpen(a)}>
      <div className="asset-visual" style={vars({ "--thue": hue })}>
        <AssetVisual
          a={a}
          badge={<span className="asset-type mono" style={{ color: hue, borderColor: `color-mix(in oklch, ${hue} 45%, transparent)` }}>{TYPE_LABEL[a.type]}</span>}
        />
      </div>
      <div className="asset-body">
        <div className="asset-name">{a.name}</div>
        {(a.feature || a.coord) && (
          <div className="asset-coord mono">
            {a.feature}{a.feature && a.coord ? " · " : ""}{a.coord ? `◎ ${a.coord}` : ""}
          </div>
        )}
        <div className="asset-foot">
          <span className="asset-price mono"><b>{a.priceDisplay}</b> {a.symbol}</span>
          <span className="asset-cta mono">VIEW →</span>
        </div>
      </div>
    </button>
  );
}

/* ---------- featured deed (Mars / Moon pair) ---------- */
function FeaturedDeed({ a, onOpen, onClaim }: { a: Asset; onOpen: (a: Asset) => void; onClaim: (a: Asset) => void }) {
  const hue = TYPE_HUE[a.type];
  const lat = findAttr(a.attributes, /lat/i);
  const lon = findAttr(a.attributes, /lon|long/i);
  const dia = findAttr(a.attributes, /diam/i);
  const rows: [string, string][] = [];
  if (a.feature) rows.push(["FEATURE", a.feature]);
  if (dia) rows.push(["DIAMETER", dia]);
  if (lat) rows.push(["LATITUDE", lat]);
  if (lon) rows.push(["LONGITUDE", lon]);
  if (rows.length < 4) rows.push(["REGISTRY", REG_LABEL[a.type]]);
  if (rows.length < 4) rows.push(["STANDARD", "ERC-721"]);
  return (
    <div className="feat panel ticks">
      <div className="feat-visual" style={vars({ "--thue": hue })}>
        <AssetVisual
          a={a}
          badge={<span className="feat-badge mono" style={{ color: hue, borderColor: `color-mix(in oklch, ${hue} 45%, transparent)` }}><span className="dot" />FEATURED · {TYPE_LABEL[a.type]}</span>}
        />
      </div>
      <div className="feat-body">
        <h3 className="feat-name">{a.name}</h3>
        <div className="feat-grid mono">
          {rows.slice(0, 4).map(([k, v], i) => <div key={i}><span>{k}</span><b title={v}>{v}</b></div>)}
        </div>
        <div className="feat-foot">
          <div className="feat-price"><span className="mono">FLOOR</span><b className="tnum">{a.priceDisplay} {a.symbol}</b></div>
          <div className="feat-btns">
            <a className="btn btn-ghost" onClick={() => onOpen(a)}>INSPECT</a>
            <a className="btn btn-primary" onClick={() => onClaim(a)}>CLAIM DEED <span className="ar">→</span></a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- detail drawer ---------- */
function Drawer({ a, onClose, onClaim }: { a: Asset; onClose: () => void; onClaim: (a: Asset) => void }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const hue = TYPE_HUE[a.type];
  const rows: [string, string][] = [];
  a.attributes.forEach((at) => {
    if (at && at.trait_type != null && at.value != null && !/image|url/i.test(String(at.trait_type))) {
      rows.push([String(at.trait_type).replace(/([a-z])([A-Z])/g, "$1 $2").toUpperCase(), String(at.value)]);
    }
  });
  rows.push(["REGISTRY", REG_LABEL[a.type]]);
  rows.push(["PROVENANCE", "NASA · IAU · USGS ✓"]);
  rows.push(["STANDARD", "ERC-721"]);
  rows.push(["NETWORK", "BASE SEPOLIA"]);
  return (
    <div className="drawer-scrim" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <button className="drawer-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="drawer-visual" style={vars({ "--thue": hue })}>
          <AssetVisual
            a={a}
            badge={<span className="asset-type mono" style={{ color: hue, borderColor: `color-mix(in oklch, ${hue} 45%, transparent)` }}>{TYPE_LABEL[a.type]}</span>}
          />
        </div>
        <div className="drawer-body">
          <div className="drawer-eyebrow mono">{REG_LABEL[a.type]}</div>
          <h2 className="drawer-name">{a.name}</h2>
          <div className="drawer-meta">
            {rows.map(([k, v], i) => (
              <div className="drawer-row mono" key={i}><span>{k}</span><span className={v.includes("✓") ? "ok" : ""}>{v}</span></div>
            ))}
          </div>
          <div className="drawer-foot">
            <div className="drawer-price"><span className="mono">PRICE</span><b className="tnum">{a.priceDisplay} {a.symbol}</b></div>
            <a className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={() => onClaim(a)}>VIEW &amp; CLAIM ON-CHAIN <span className="ar">→</span></a>
            <div className="drawer-note mono">BASE SEPOLIA · ERC-721 DEED</div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function MktStat({ label, value, accent }: { label: string; value: React.ReactNode; accent?: boolean }) {
  return (
    <div className="mkt-stat">
      <div className="mkt-stat-v tnum" style={accent ? { color: "var(--accent-bright)" } : undefined}>{value}</div>
      <div className="mkt-stat-l mono">{label}</div>
    </div>
  );
}

const Marketplace: NextPage = () => {
  const router = useRouter();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [regs, setRegs] = useState<TKind[]>([]);
  const [features, setFeatures] = useState<string[]>([]);
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const [sort, setSort] = useState("recent");
  const [drawer, setDrawer] = useState<Asset | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const listings = await getAllValidListings({ contract: MarketplaceContract, start: 0, count: BigInt(100) });
        const mapped: Asset[] = (listings || []).map((l: any) => {
          const m = l?.asset?.metadata || {};
          const attrs: Attr[] = Array.isArray(m.attributes) ? m.attributes : [];
          const name = m.name || "Unnamed Asset";
          const category = m.category;
          const type = deriveType(name, category);
          const lat = findAttr(attrs, /lat/i);
          const lon = findAttr(attrs, /lon|long/i);
          const coord = lat && lon ? `${lat} · ${lon}` : findAttr(attrs, /coord/i);
          return {
            id: l.id.toString(),
            name,
            image: ipfs(m.image),
            price: parseFloat(l?.currencyValuePerToken?.displayValue || "0") || 0,
            priceDisplay: l?.currencyValuePerToken?.displayValue || "0",
            symbol: l?.currencyValuePerToken?.symbol || "ETH",
            type,
            category,
            feature: findAttr(attrs, /feature/i),
            coord,
            attributes: attrs,
          };
        });
        if (alive) setAssets(mapped);
      } catch (err) {
        console.error("Error fetching listings:", err);
        if (alive) toast.error("Failed to load listings");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const prices = assets.map((a) => a.price);
  const minP = assets.length ? Math.floor(Math.min(...prices) * 100) / 100 : 0;
  const maxP = assets.length ? Math.ceil(Math.max(...prices) * 100) / 100 : 1;
  // Default the price ceiling to the highest listing once data loads.
  useEffect(() => { if (assets.length) setMaxPrice(maxP); }, [assets.length, maxP]);

  const registries = useMemo(() => {
    const counts: Partial<Record<TKind, number>> = {};
    assets.forEach((a) => { counts[a.type] = (counts[a.type] || 0) + 1; });
    return REG_ORDER.filter((k) => counts[k]).map((k) => ({ k, label: REG_LABEL[k], count: counts[k] as number }));
  }, [assets]);

  const featureOpts = useMemo(() => {
    const set: string[] = [];
    assets.forEach((a) => { if (a.feature && !set.includes(a.feature)) set.push(a.feature); });
    return set.slice(0, 14);
  }, [assets]);

  const featured = useMemo(() => {
    const picks: Asset[] = [];
    const mars = assets.find((a) => a.type === "MARS");
    const moon = assets.find((a) => a.type === "MOON");
    if (mars) picks.push(mars);
    if (moon) picks.push(moon);
    if (picks.length < 2) {
      [...assets].sort((a, b) => b.price - a.price).forEach((a) => { if (picks.length < 2 && !picks.includes(a)) picks.push(a); });
    }
    return picks;
  }, [assets]);

  const filtered = useMemo(() => {
    let list = assets.filter((a) => {
      if (regs.length && !regs.includes(a.type)) return false;
      if (features.length && !(a.feature && features.includes(a.feature))) return false;
      if (maxPrice != null && a.price > maxPrice) return false;
      if (q.trim()) {
        const s = `${a.name} ${a.feature || ""} ${a.category || ""} ${a.coord || ""} ${a.id}`.toLowerCase();
        if (!s.includes(q.trim().toLowerCase())) return false;
      }
      return true;
    });
    if (sort === "price-asc") list = [...list].sort((x, y) => x.price - y.price);
    else if (sort === "price-desc") list = [...list].sort((x, y) => y.price - x.price);
    else if (sort === "az") list = [...list].sort((x, y) => x.name.localeCompare(y.name));
    return list;
  }, [assets, regs, features, maxPrice, q, sort]);

  const toggleReg = (k: TKind) => setRegs((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const toggleFeat = (f: string) => setFeatures((s) => (s.includes(f) ? s.filter((x) => x !== f) : [...s, f]));
  const reset = () => { setRegs([]); setFeatures([]); setQ(""); setMaxPrice(maxP); setSort("recent"); };
  const filtersOn = regs.length || features.length || q || (maxPrice != null && maxPrice < maxP);

  const avgFloor = assets.length ? (assets.reduce((s, a) => s + a.price, 0) / assets.length).toFixed(2) : "0.00";
  const goClaim = (a: Asset) => router.push(`/marketplace/${a.id}`);

  return (
    <div className="mkt-page">
      <Head>
        <title>NiftySky — Marketplace</title>
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <section className="mkt-hero">
        <div className="mkt-hero-top">
          <div>
            <div className="eyebrow">VERIFIED ASSET REGISTRY</div>
            <h1 className="mkt-title">The Marketplace<br />for <span className="grad">Real Cosmos</span></h1>
          </div>
          <div className="mkt-stats">
            <MktStat label="LISTED ASSETS" value={loading ? "—" : assets.length} />
            <span className="mkt-stat-div" />
            <MktStat label="AVAILABLE NOW" value={loading ? "—" : assets.length} accent />
            <span className="mkt-stat-div" />
            <MktStat label="AVG FLOOR" value={loading ? "—" : `${avgFloor} Ξ`} />
            <span className="mkt-stat-div" />
            <MktStat label="REGISTRIES" value={loading ? "—" : registries.length} />
          </div>
        </div>

        {featured.length > 0 && (
          <>
            <div className="feat-head-row">
              <div className="sec-tag" style={{ margin: 0 }}><span className="idx">★</span> FEATURED DEEDS <span className="rule" /> <span>VERIFIED · NASA · USGS</span></div>
            </div>
            <div className="feat-pair">
              {featured.map((a) => <FeaturedDeed key={a.id} a={a} onOpen={setDrawer} onClaim={goClaim} />)}
            </div>
          </>
        )}
      </section>

      <section className="mkt-browse wrap-fluid">
        <aside className="rail">
          <div className="rail-head mono"><span>FILTERS</span><button className="rail-reset" onClick={reset}>RESET</button></div>

          {registries.length > 1 && (
            <div className="rail-sec">
              <div className="rail-label mono">REGISTRY</div>
              {registries.map((r) => (
                <label key={r.k} className={"rail-check" + (regs.includes(r.k) ? " on" : "")}>
                  <input type="checkbox" checked={regs.includes(r.k)} onChange={() => toggleReg(r.k)} />
                  <span className="rail-box" style={vars({ "--rc": TYPE_HUE[r.k] })} />
                  <span className="rail-ctxt">{r.label}</span>
                  <span className="rail-hint mono">{r.count}</span>
                </label>
              ))}
            </div>
          )}

          {maxP > minP && (
            <div className="rail-sec">
              <div className="rail-label mono">MAX PRICE · <b className="tnum">{(maxPrice ?? maxP).toFixed(2)} {assets[0]?.symbol || "ETH"}</b></div>
              <input type="range" className="rail-range" min={minP} max={maxP} step={0.05} value={maxPrice ?? maxP} onChange={(e) => setMaxPrice(+e.target.value)} />
              <div className="rail-range-ends mono"><span>{minP.toFixed(2)}</span><span>{maxP.toFixed(2)}</span></div>
            </div>
          )}

          {featureOpts.length > 0 && (
            <div className="rail-sec">
              <div className="rail-label mono">FEATURE</div>
              <div className="rail-rars">
                {featureOpts.map((f) => (
                  <button key={f} className={"rail-rar mono" + (features.includes(f) ? " on" : "")} onClick={() => toggleFeat(f)}>{f.toUpperCase()}</button>
                ))}
              </div>
            </div>
          )}

          <div className="rail-foot mono"><span className="dot dot-live" />{filtered.length} ASSET{filtered.length === 1 ? "" : "S"} MATCH</div>
        </aside>

        <div className="mkt-results">
          <div className="mkt-toolbar">
            <div className="mkt-search">
              <span className="mkt-search-ic">⌕</span>
              <input className="mono" placeholder="Search assets, features, coordinates…" value={q} onChange={(e) => setQ(e.target.value)} />
              {q && <button className="mkt-search-x" onClick={() => setQ("")}>✕</button>}
            </div>
            <div className="mkt-sort">
              <span className="mono">SORT</span>
              <div className="mkt-sort-seg">
                {SORTS.map((s) => <button key={s.k} className={"mkt-sort-b mono" + (sort === s.k ? " on" : "")} onClick={() => setSort(s.k)}>{s.label}</button>)}
              </div>
            </div>
          </div>
          <div className="mkt-bar-l mono">{loading ? "LOADING REGISTRY…" : `${filtered.length} RESULT${filtered.length === 1 ? "" : "S"}${filtersOn ? " · FILTERED" : ""}`}</div>

          {loading ? (
            <div className="asset-grid">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="mkt-skel" />)}
            </div>
          ) : filtered.length ? (
            <div className="asset-grid">
              {filtered.map((a) => <AssetCard key={a.id} a={a} onOpen={setDrawer} />)}
            </div>
          ) : (
            <div className="mkt-empty mono">
              <div className="mkt-empty-ic">⌖</div>
              {assets.length ? "NO ASSETS MATCH THESE FILTERS" : "NO ACTIVE LISTINGS YET — CHECK BACK SOON"}
              {assets.length > 0 && <button className="btn btn-ghost" onClick={reset} style={{ marginTop: 18 }}>CLEAR FILTERS</button>}
            </div>
          )}
        </div>
      </section>

      {drawer && <Drawer a={drawer} onClose={() => setDrawer(null)} onClaim={(a) => { setDrawer(null); goClaim(a); }} />}
    </div>
  );
};

export default Marketplace;
